<template>
  <div class="space-y-4">
    <!-- Env Vars Section -->
    <div>
      <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Environment Variables
      </h4>

      <div v-if="isLoadingEnv" class="text-xs text-gray-500 dark:text-gray-400">Loading...</div>

      <div v-else-if="envKeys.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
        No environment variables configured.
      </div>

      <ul v-else class="space-y-1">
        <li
          v-for="key in envKeys"
          :key="key"
          class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded px-3 py-1.5 text-xs"
        >
          <span class="font-mono text-gray-900 dark:text-gray-100">{{ key }}</span>
          <Button variant="ghost" size="sm" class="text-red-600" @click="removeEnvVar(key)">
            Remove
          </Button>
        </li>
      </ul>

      <form @submit.prevent="addEnvVar" class="mt-2 flex items-end gap-2">
        <div class="flex-1">
          <Input v-model="newEnvKey" placeholder="KEY" class="w-full" />
        </div>
        <div class="flex-1">
          <Input v-model="newEnvValue" type="password" placeholder="value" class="w-full" />
        </div>
        <Button type="submit" variant="outline" size="sm" :disabled="!newEnvKey.trim()">
          Add
        </Button>
      </form>
    </div>

    <!-- Secrets Section -->
    <div>
      <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Secrets</h4>

      <div v-if="isLoadingSecrets" class="text-xs text-gray-500 dark:text-gray-400">
        Loading...
      </div>

      <div v-else-if="secretKeys.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
        No secrets configured.
      </div>

      <ul v-else class="space-y-1">
        <li
          v-for="sk in secretKeys"
          :key="sk.key"
          class="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded px-3 py-1.5 text-xs"
        >
          <span class="font-mono text-gray-900 dark:text-gray-100">{{ sk.key }}</span>
          <Button variant="ghost" size="sm" class="text-red-600" @click="removeSecret(sk.key)">
            Remove
          </Button>
        </li>
      </ul>

      <form @submit.prevent="addSecret" class="mt-2 flex items-end gap-2">
        <div class="flex-1">
          <Input v-model="newSecretKey" placeholder="SECRET_KEY" class="w-full" />
        </div>
        <div class="flex-1">
          <Input v-model="newSecretValue" type="password" placeholder="value" class="w-full" />
        </div>
        <Button type="submit" variant="outline" size="sm" :disabled="!newSecretKey.trim()">
          Add
        </Button>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { Button, Input } from '@asabdev/asab-ui';
  import { mcpRestClient } from '@/lib/mcp/restClient';
  import type { ToolSecretKey } from '@/lib/mcp/types';

  interface Props {
    toolId: string;
    orgId: string;
  }

  const props = defineProps<Props>();

  const envKeys = ref<string[]>([]);
  const secretKeys = ref<ToolSecretKey[]>([]);
  const isLoadingEnv = ref(false);
  const isLoadingSecrets = ref(false);

  const newEnvKey = ref('');
  const newEnvValue = ref('');
  const newSecretKey = ref('');
  const newSecretValue = ref('');

  const loadEnv = async () => {
    isLoadingEnv.value = true;
    try {
      const data = await mcpRestClient.listEnvVars(props.orgId, props.toolId);
      envKeys.value = Object.keys(data);
    } catch {
      /* ignore */
    } finally {
      isLoadingEnv.value = false;
    }
  };

  const loadSecrets = async () => {
    isLoadingSecrets.value = true;
    try {
      secretKeys.value = await mcpRestClient.listSecrets(props.orgId, props.toolId);
    } catch {
      /* ignore */
    } finally {
      isLoadingSecrets.value = false;
    }
  };

  const addEnvVar = async () => {
    if (!newEnvKey.value.trim()) return;
    await mcpRestClient.setEnvVar(props.orgId, props.toolId, newEnvKey.value.trim(), newEnvValue.value);
    newEnvKey.value = '';
    newEnvValue.value = '';
    await loadEnv();
  };

  const removeEnvVar = async (key: string) => {
    await mcpRestClient.deleteEnvVar(props.orgId, props.toolId, key);
    await loadEnv();
  };

  const addSecret = async () => {
    if (!newSecretKey.value.trim()) return;
    await mcpRestClient.setSecret(props.orgId, props.toolId, newSecretKey.value.trim(), newSecretValue.value);
    newSecretKey.value = '';
    newSecretValue.value = '';
    await loadSecrets();
  };

  const removeSecret = async (key: string) => {
    await mcpRestClient.deleteSecret(props.orgId, props.toolId, key);
    await loadSecrets();
  };

  onMounted(() => {
    loadEnv();
    loadSecrets();
  });
</script>
